import type { Rng } from '../../math/rng.ts';
import type { ProblemTemplate } from '../../types.ts';
import { lerp, scaled, smallerFromLarger, splitUnequal, wholeProblem } from './helpers-g01-04.ts';

/**
 * Grade 1 templates — ids and specs: docs/CONTENT_CATALOGUE.md and levels/levels.ts TEMPLATE_CATALOGUE.
 *
 * CAPS Grade 1: number sentences with a missing number (□ only, no letters),
 * addition and subtraction to 10, then to 20. No zeros and no negatives.
 */

/** Total for the "facts to 10" sentences: 3–6 at difficulty 0, 6–10 at difficulty 1. */
function totalTo10(rng: Rng, difficulty: number): number {
  return rng.int(scaled(3, 6, difficulty), scaled(6, 10, difficulty));
}

/** Smallest part of a split: the trivial "± 1" facts only appear in the gentle half of the range. */
function minPart(difficulty: number): number {
  return difficulty < 0.5 ? 1 : 2;
}

const addMissingEnd: ProblemTemplate = {
  id: 'g1.add-missing-end',
  grade: 1,
  title: 'Missing addend (second): a + □ = c, to 10',
  parTimeMs: 7000,
  generate(rng, difficulty) {
    const total = totalTo10(rng, difficulty);
    const [shown, answer] = splitUnequal(rng, total, minPart(difficulty));
    return wholeProblem(`${shown} + \\box = ${total}`, answer, [
      shown + total, // added the two numbers it can see (wrong operation)
      answer + 1, // counted on and included the starting number
      answer - 1, // counted on and stopped one short
    ]);
  },
};

const addMissingStart: ProblemTemplate = {
  id: 'g1.add-missing-start',
  grade: 1,
  title: 'Missing addend (first): □ + b = c, to 10',
  parTimeMs: 7500,
  generate(rng, difficulty) {
    const total = totalTo10(rng, difficulty);
    const [answer, shown] = splitUnequal(rng, total, minPart(difficulty));
    return wholeProblem(`\\box + ${shown} = ${total}`, answer, [
      shown + total, // added the two numbers it can see (wrong operation)
      answer + 1, // counted on and included the starting number
      answer - 1, // counted on and stopped one short
    ]);
  },
};

const subMissing: ProblemTemplate = {
  id: 'g1.sub-missing',
  grade: 1,
  title: 'Missing number taken away: c − □ = b, to 10',
  parTimeMs: 8000,
  generate(rng, difficulty) {
    const start = rng.int(scaled(4, 7, difficulty), scaled(7, 10, difficulty));
    const [answer, left] = splitUnequal(rng, start, minPart(difficulty));
    return wholeProblem(`${start} - \\box = ${left}`, answer, [
      start + left, // added instead of subtracting (inverse-operation mix-up)
      answer + 1, // counted back and included the starting number
      answer - 1, // counted back and stopped one short
    ]);
  },
};

const addTo20: ProblemTemplate = {
  id: 'g1.add-to-20',
  grade: 1,
  title: 'Missing addend to 20: a + □ = c, 11 ≤ c ≤ 20',
  parTimeMs: 8500,
  generate(rng, difficulty) {
    const total = rng.int(scaled(11, 13, difficulty), scaled(14, 20, difficulty));
    // The missing number is a single digit (≥ 2); it grows with difficulty.
    const maxMissing = scaled(4, 9, difficulty);
    const options: number[] = [];
    for (let b = 2; b <= maxMissing; b++) if (total - b !== b) options.push(b);
    // "Bridging through ten" (8 + □ = 15) is the hard case; 12 + □ = 15 only needs the ones.
    const bridging = options.filter((b) => total - b <= 9);
    const withinTeens = options.filter((b) => total - b >= 10);
    const preferBridging = rng.chance(lerp(0.2, 0.9, difficulty));
    const [preferred, fallback] = preferBridging ? [bridging, withinTeens] : [withinTeens, bridging];
    const answer = rng.pick(preferred.length ? preferred : fallback); // b = 2 is always an option
    const shown = total - answer;
    return wholeProblem(`${shown} + \\box = ${total}`, answer, [
      smallerFromLarger(total, shown), // column "subtraction" without bridging: 15 − 8 → 13
      shown + total, // added the two numbers it can see (wrong operation)
      answer + 1, // counted on and included the starting number
      answer - 1, // counted on and stopped one short
    ]);
  },
};

export const GRADE_01: ProblemTemplate[] = [addMissingEnd, addMissingStart, subMissing, addTo20];
